import { useState } from 'react';
import currency from '|/helpers/currency';
import { ReviewSummaryResponse } from '|/services/purchase-api';

interface Discount {
  type: string;
  description: string;
  amount: number;
}

const useOrderSummaryVm = (summary: ReviewSummaryResponse | null) => {
  const [collapsedFees, setCollapsedFees] = useState(true);
  const [collapsedShipping, setCollapsedShipping] = useState(true);
  const [collapsedIncentives, setCollapsedIncentives] = useState(true);
  const [isPaymentBreakdownCollapsed, setIsPaymentBreakdownCollapsed] = useState(true);

  const {
    customer: {
      firstName = null,
      middleName = null,
      lastName = null,
      suffix = null,
      address = null,
      address: homeAddress = {},
      mailing: mailingAddress = {},
      phoneNumber = null,
      emailAddress = null,
      isEvTaxCreditEligible = null
    } = {},
    cobuyer: {
      firstName: cobuyerFirstName = null,
      middleName: cobuyerMiddleName = null,
      lastName: cobuyerLastName = null,
      suffix: cobuyerSuffix = null
    } = {},
    vehicle: {
      year: vehicleYear = null,
      make: vehicleMake = null,
      model: vehicleModel = null,
      odometer: vehicleOdometer = null,
      trim: vehicleTrim = null,
      inventoryType = null,
      msrp = null,
      vehicleType = null
    } = {},
    tradeIn: {
      lienAmount = null,
      value: tradeInValue = null,
      year: tradeInYear = null,
      make: tradeInMake = null,
      model: tradeInModel = null,
      status: tradeInStatus = null,
      trim: tradeInTrim = null,
      isTradeInEligible = null
    } = {},
    product: {
      type: productType = null,
      price: productPrice = null,
      status: productStatus = null,
      mileage: productMileage = null,
      months: productMonths = null
    } = {},
    gap: { status: gapStatus = null, price: gapPrice = null } = {},
    payment: {
      creditFromPreviousOrder = null,
      dueAtOrder = null,
      cashDown = null,
      refundDue = null,
      paymentAccounts = []
    } = {},
    financing: { isEligibleForCoApplicant = null, isCoApplicantToggleOn = null } = {},
    funding: {
      type: fundingType = null,
      institution: fundingInstitution = null,
      thirdParty: {
        lenderName = null,
        breakdown: { cash: thirdPartyCash = null, loanAmount: thirdPartyLoanAmount = null } = {}
      } = {},
      loan: {
        term: loanTerm = null,
        payment: loanPayment = null,
        apr: loanApr = null,
        amountFinanced = null
      } = {}
    } = {},
    transfer: {
      activityType: activityTypeFromApi = null,
      deliveryType = null,
      address: transferAddress = null,
      locationType = null
    } = {},
    fees: feesList = [],
    carvanaDiscount = null,
    totals: {
      totalPrice = null,
      vehicleStickerPrice = null,
      taxes = null,
      fees: totalFees = null,
      discounts: totalDiscounts = null,
      rebates: totalRebates = null,
      products: totalProducts = null,
      deliveryCharge = null,
      shippingFee = null,
      tradeInValue: totalTradeInValue = null,
      tradeInTaxSavings = null,
      savingsAndIncentives = null,
      incentives: totalIncentives = null
    } = {},
    incentives = [],
    rebates = [],
    discounts = [],
    enrolledInAutopay = null
  } = summary ?? {};

  const hasAddress = !!address;
  const customerName = [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');
  const hasPii = customerName && phoneNumber && hasAddress;
  const cobuyerName = [cobuyerFirstName, cobuyerMiddleName, cobuyerLastName, cobuyerSuffix]
    .filter(Boolean)
    .join(' ');
  const hasCoBuyerName = cobuyerName.length > 1;
  const isCarvanaFinancing =
    fundingType?.toLowerCase() === 'financing' && fundingInstitution?.toLowerCase() === 'carvana';
  const isCash = fundingType?.toLowerCase() === 'cash';
  const isThirdParty = fundingType?.toLowerCase() === 'thirdparty';
  const showPaymentDetails =
    !!cashDown || !!creditFromPreviousOrder || !!shippingFee || !!thirdPartyCash;
  const showDeliveryFee = !!deliveryCharge && deliveryCharge > 0;
  const stateName = homeAddress?.state || '';
  const areHomeAndMailingAddressesTheSame =
    homeAddress.line1 === mailingAddress.line1 &&
    homeAddress.line2 === mailingAddress.line2 &&
    homeAddress.zipCode === mailingAddress.zipCode;
  const hasAncillaryProducts =
    gapStatus?.toLowerCase() !== 'notset' || productStatus?.toLowerCase() !== 'notset';
  const declinedAncillary = (productStatus?.toLowerCase() ?? 'notset') === 'declined';
  const tradeDeclined = tradeInStatus?.toLowerCase() === 'declined';
  const completedTrade = tradeInStatus?.toLowerCase() !== 'notset';
  const calculatedTradeValue = tradeInValue ? tradeInValue - (lienAmount ?? 0) : 0;
  const isNegativeEquityTrade = calculatedTradeValue < 0;
  const tradeBreakdownTitle = `Trade-in${
    tradeDeclined ? '' : ` ${isNegativeEquityTrade ? 'balance due' : 'value'}`
  }`;

  const getTradeMessage = () => {
    if (tradeDeclined && (tradeInTaxSavings ?? 0) > 0)
      return `<p>You declined adding a trade-in. You could save ${stateName} sales tax by including a trade-in.</p>`;
    if (tradeDeclined)
      return `<p>You declined adding a trade-in. Adding a trade-in can reduce or eliminate down and monthly payments.</p>`;

    return calculatedTradeValue >= 0
      ? `<p>Your trade-in value of ${currency(
          calculatedTradeValue
        )} will be applied to your purchase, reducing your down payment and the amount you need to finance.</p>`
      : `<p>Your loan balance is higher than your car's value, also known as "negative equity."</p><p>Part of the ${currency(
          calculatedTradeValue
        )} you owe on your trade-in will be added to your new loan. The remaining balance will be added to your cash down.</p>`;
  };
  const tradeMessage = getTradeMessage();
  const totalTaxes = taxes ?? 0;
  const totalFeesAmount = totalFees ?? 0;

  const creditKarmaDiscount = discounts?.find(
    (discount: Discount) => discount?.type === 'CreditKarmaTaxDiscount'
  ) as Discount | undefined;
  const creditKarmaAmountSaved = creditKarmaDiscount?.amount;

  const tradeBoostDiscount = discounts?.find((discount: Discount) =>
    discount.description?.includes('You Save Trade In')
  ) as Discount | undefined;
  const tradeBoostAmountSaved = tradeBoostDiscount?.amount;

  const employeeDiscount = discounts?.find((discount: Discount) =>
    discount.description?.includes('EmployeeDiscount')
  ) as Discount | undefined;
  const employeeDiscountAmount = employeeDiscount?.amount;

  const totalDiscountsAmount =
    (totalDiscounts ?? 0) -
    (creditKarmaAmountSaved ?? 0) -
    (tradeBoostAmountSaved ?? 0) -
    (employeeDiscountAmount ?? 0);

  const cashSubtotal = (totalPrice ?? 0) - (shippingFee ?? 0) - (deliveryCharge ?? 0);
  const subtotal =
    (totalPrice ?? 0) -
    (totalProducts ?? 0) -
    (deliveryCharge ?? 0) +
    (totalTradeInValue ?? 0) +
    (totalRebates ?? 0);
  const totalFinanced = isThirdParty ? thirdPartyLoanAmount ?? 0 : amountFinanced ?? 0;
  const cashDownAmount =
    isThirdParty && (thirdPartyCash ?? 0) > 0 ? thirdPartyCash ?? 0 : cashDown ?? 0;

  const showTradeInEdit = completedTrade;

  const showProtectionEdit = productStatus !== null && productStatus.toLowerCase() !== 'notset';

  const activityType = activityTypeFromApi || deliveryType;
  const hasTransferMethod = activityType
    ? activityType.toLowerCase().includes('pickup') ||
      activityType.toLowerCase().includes('delivery')
    : false;
  const isPickup = hasTransferMethod ? !!activityType?.toLowerCase().includes('pickup') : false;
  const isDelivery = hasTransferMethod ? !!activityType?.toLowerCase().includes('delivery') : false;
  const isVendingMachine = locationType?.toLowerCase() === 'vendingmachine';
  const isMarketplaceVehicle = inventoryType?.toLowerCase() === 'dealermarketplace';

  const hasPaymentMethodBeenSelected = !!fundingType;
  const hasTerms = !!loanTerm;
  const isOutsideFinance = fundingType?.toLowerCase() === 'financing' && !isCarvanaFinancing;
  const dueLabel = refundDue ? 'Refund due' : 'Due to Carvana';
  const dueLabelTotalSummary = refundDue ? 'Refund due' : 'Total due at order';
  const dueAmount = refundDue ? refundDue : dueAtOrder ?? 0;
  const hasEvTaxCredit = !!rebates?.some(rebate => rebate.name === 'EVTaxCredit');
  const evTaxCreditAmount = hasEvTaxCredit
    ? rebates?.find(rebate => rebate.name === 'EVTaxCredit')?.amount ?? 0
    : 0;
  const downPaymentAccount = paymentAccounts?.find(account => account.type === 'DownPayment');
  const autopayAccount = paymentAccounts?.find(account => account.type === 'Autopay');

  const hasCarvanaFinancingVerified =
    isCarvanaFinancing && !!loanApr && !!loanTerm && !!loanPayment;
  const hasSafVerified =
    (isThirdParty || isOutsideFinance) && !!thirdPartyLoanAmount && !!lenderName;
  const hasCashVerified = isCash && !!cashDown;
  const hasPaymentMethodBeenVerified =
    hasPaymentMethodBeenSelected &&
    (hasCarvanaFinancingVerified || hasSafVerified || hasCashVerified);

  return {
    customerName,
    hasPii,
    phoneNumber,
    emailAddress,
    homeAddress,
    mailingAddress,
    isCoApplicantToggleOn,
    isEligibleForCoApplicant,
    cobuyerName,
    hasCoBuyerName,
    isCarvanaFinancing,
    isCash,
    isThirdParty,
    showPaymentDetails,
    showDeliveryFee,
    stateName,
    areHomeAndMailingAddressesTheSame,
    hasAncillaryProducts,
    declinedAncillary,
    collapsedFees,
    setCollapsedFees,
    collapsedShipping,
    setCollapsedShipping,
    tradeDeclined,
    completedTrade,
    calculatedTradeValue,
    tradeMessage,
    totalTaxes,
    totalFeesAmount,
    totalDiscountsAmount,
    cashSubtotal,
    subtotal,
    totalTradeInValue,
    totalFinanced,
    showTradeInEdit,
    showProtectionEdit,
    isPaymentBreakdownCollapsed,
    setIsPaymentBreakdownCollapsed,
    shippingFee,
    deliveryCharge,
    gapStatus,
    gapPrice,
    productStatus,
    productPrice,
    productType,
    productMileage,
    productMonths,
    totalPrice,
    tradeInStatus,
    tradeInYear,
    tradeInMake,
    tradeInModel,
    tradeInValue,
    tradeBreakdownTitle,
    transferAddress,
    lienAmount,
    dueAtOrder,
    cashDown,
    cashDownAmount,
    creditFromPreviousOrder,
    thirdPartyCash,
    vehicleStickerPrice,
    feesList,
    hasTransferMethod,
    isPickup,
    isDelivery,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    vehicleTrim,
    vehicleOdometer,
    isMarketplaceVehicle,
    isVendingMachine,
    inventoryType,
    vehicleType,
    hasPaymentMethodBeenSelected,
    hasTerms,
    isOutsideFinance,
    fundingInstitution,
    lenderName,
    amountFinanced,
    loanApr,
    loanTerm,
    loanPayment,
    thirdPartyLoanAmount,
    enrolledInAutopay,
    dueLabel,
    refundDue,
    dueAmount,
    tradeInTaxSavings,
    hasEvTaxCredit,
    evTaxCreditAmount,
    isEvTaxCreditEligible,
    downPaymentAccount,
    autopayAccount,
    fundingType,
    dueLabelTotalSummary,
    hasCarvanaFinancingVerified,
    hasSafVerified,
    hasPaymentMethodBeenVerified,
    creditKarmaDiscount,
    creditKarmaAmountSaved,
    tradeBoostDiscount,
    tradeBoostAmountSaved,
    employeeDiscount,
    employeeDiscountAmount,
    isTradeInEligible,
    msrp,
    carvanaDiscount,
    incentives,
    savingsAndIncentives,
    collapsedIncentives,
    setCollapsedIncentives,
    totalIncentives
  };
};

export default useOrderSummaryVm;
